import { Dropdown, Tag } from '@carbon/react';
import { DateRangeFilter, PageHeader, useFilters, useFormat, useReferenceOptions } from '@pos/admin-ui';
import type { HoComponents } from '@pos/api-client';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';
import { dayRange, setOnly, shortId } from '../logs/dates';
import { LogDetail } from '../logs/LogDetail';
import { LogTable, type LogQuery } from '../logs/LogTable';
import './payments.scss';

type Row = HoComponents['schemas']['PaymentReconciliationRow'];
type Totals = HoComponents['schemas']['PaymentReconciliationTotals'];

/** Carbon tag colours for each reconciliation result: a problem stands out, a settled row stays quiet. */
const MATCH_TAG: Record<string, 'green' | 'red' | 'magenta' | 'purple' | 'blue' | 'gray'> = {
  matched: 'green',
  not_recorded: 'red',
  amount_differs: 'magenta',
  refunded: 'purple',
  open: 'blue',
  not_paid: 'gray',
};

const SOURCES = ['store_payment', 'online_order'] as const;
const STATES = ['open', 'succeeded', 'refunded', 'not_paid'] as const;

interface Option {
  id: string;
  label: string;
}

/** One value or all of them, as the reconciliation API takes it. */
function OneChoice({ id, label, all, options, value, onChange }: { id: string; label: string; all: string; options: Option[]; value: string; onChange(v: string): void }) {
  const items: Option[] = [{ id: '', label: all }, ...options];
  return (
    <Dropdown<Option>
      id={id}
      titleText={label}
      label={label}
      items={items}
      itemToString={(o) => o?.label ?? ''}
      selectedItem={items.find((o) => o.id === value) ?? items[0]}
      onChange={({ selectedItem }) => onChange(selectedItem?.id ?? '')}
    />
  );
}

/**
 * Payment reconciliation (ADR 0074): every payment through a provider on every channel, newest first, against the tender the
 * store recorded for it. Read only: search (order number or provider transaction), filters, paging 25/50/100; a row opens its
 * details. Totals are for the whole filter, from the server.
 */
export function PaymentReconciliationPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const stores = useReferenceOptions('org.store');
  const entities = useReferenceOptions('org.legal_entity');
  const channels = useReferenceOptions('config.sales_channel');
  const providers = useQuery({
    queryKey: ['ho', 'payment-providers'],
    queryFn: async () => {
      const { data, error } = await hoApi.GET('/api/v1/integration/payment/providers');
      if (!data) throw error;
      return data;
    },
  });
  const accounts = useQuery({
    queryKey: ['ho', 'payment-accounts', 'all'],
    queryFn: async () => {
      const { data, error } = await hoApi.GET('/api/v1/integration/payment/accounts');
      if (!data) throw error;
      return data.items;
    },
  });
  const filters = useFilters({ provider: '', store: '', entity: '', source: '', state: '', from: '', to: '' });
  const [totals, setTotals] = useState<Totals>();
  const [open, setOpen] = useState<Row>();
  const r = (key: string) => t(`payments.reconciliation.${key}` as I18nKey);

  const nameIn = (list: { value: string; label: string }[] | undefined, id: string | null | undefined) =>
    id ? (list?.find((o) => o.value === id)?.label ?? shortId(id)) : '—';
  const providerName = (code: string) => providers.data?.find((p) => p.code === code)?.name ?? code;
  const accountName = (row: Row) => accounts.data?.find((a) => a.id === row.providerAccountId)?.name ?? providerName(row.providerCode);
  const where = (row: Row) =>
    row.salesChannelId ? nameIn(channels.data, row.salesChannelId) : r(`source.${row.source === 'store_payment' ? 'store_payment' : 'online_order'}`);
  const providerState = (state: string) =>
    r(`state.${state === 'succeeded' ? 'succeeded' : state === 'creating' || state === 'pending' ? 'open' : 'not_paid'}`);
  const money = (amount: number | string | null | undefined, row: Row) => format.money(amount, row.currencyCode);
  const match = (row: Row) => (
    <Tag type={MATCH_TAG[row.match] ?? 'gray'} size="sm">
      {r(`match.${row.match in MATCH_TAG ? row.match : 'not_paid'}`)}
    </Tag>
  );

  const load = async (q: LogQuery) => {
    const { provider, store, entity, source, state, from, to } = filters.applied;
    const { data, error } = await hoApi.GET('/api/v1/integration/payment/reconciliation', {
      params: { query: { ...setOnly({ provider, storeId: store, legalEntityId: entity, source, state, ...dayRange(from, to) }), ...q } },
    });
    if (!data) throw error;
    setTotals(data.totals);
    return data;
  };

  const all = r('filters.all');
  return (
    <section className="pos-admin-page">
      <PageHeader title={r('title')} description={r('intro')} />
      {totals && (
        <p className="pos-reconciliation__totals" role="status">
          {t('payments.reconciliation.totals', {
            count: Number(totals.count),
            succeeded: Number(totals.succeeded),
            paid: format.money(totals.paid, 'VND'),
            refunded: format.money(totals.refunded, 'VND'),
          })}
        </p>
      )}
      <LogTable<Row>
        label={r('title')}
        queryKey={['ho', 'payment-reconciliation']}
        load={load}
        rowId={(row) => row.intentId}
        filters={filters}
        pageSizes={[25, 50, 100]}
        filterFields={
          <>
            <OneChoice id="rec-provider" label={r('filters.provider')} all={all} value={filters.draft.provider}
              options={(providers.data ?? []).map((p) => ({ id: p.code, label: p.name }))} onChange={(v) => filters.set('provider', v)} />
            <OneChoice id="rec-entity" label={r('filters.entity')} all={all} value={filters.draft.entity}
              options={(entities.data ?? []).map((o) => ({ id: o.value, label: o.label }))} onChange={(v) => filters.set('entity', v)} />
            <OneChoice id="rec-store" label={r('filters.store')} all={all} value={filters.draft.store}
              options={(stores.data ?? []).map((o) => ({ id: o.value, label: o.label }))} onChange={(v) => filters.set('store', v)} />
            <OneChoice id="rec-source" label={r('filters.source')} all={all} value={filters.draft.source}
              options={SOURCES.map((s) => ({ id: s, label: r(`source.${s}`) }))} onChange={(v) => filters.set('source', v)} />
            <OneChoice id="rec-state" label={r('filters.state')} all={all} value={filters.draft.state}
              options={STATES.map((s) => ({ id: s, label: r(`state.${s}`) }))} onChange={(v) => filters.set('state', v)} />
            <DateRangeFilter
              id="rec-date"
              legendText={r('filters.date')}
              from={filters.draft.from}
              to={filters.draft.to}
              onChange={(range) => filters.setDraft((d) => ({ ...d, ...range }))}
            />
          </>
        }
        onOpen={setOpen}
        columns={[
          { key: 'time', header: r('columns.time'), render: (row) => format.dateTime(row.createdAt) },
          { key: 'reference', header: r('columns.reference'), render: (row) => row.reference },
          { key: 'channel', header: r('columns.channel'), render: where },
          { key: 'store', header: r('columns.store'), render: (row) => nameIn(stores.data, row.storeId) },
          { key: 'account', header: r('columns.account'), render: accountName },
          { key: 'amount', header: r('columns.amount'), render: (row) => money(row.amount, row) },
          { key: 'refunded', header: r('columns.refunded'), render: (row) => (Number(row.refundedAmount) > 0 ? money(row.refundedAmount, row) : '—') },
          { key: 'match', header: r('columns.match'), render: match },
        ]}
      />
      {open && (
        <LogDetail
          title={`${open.reference} · ${accountName(open)}`}
          label={r('title')}
          onClose={() => setOpen(undefined)}
          fields={[
            [r('columns.match'), r(`match.${open.match in MATCH_TAG ? open.match : 'not_paid'}`)],
            [r('detail.state'), open.isLate ? `${providerState(open.state)} · ${r('detail.late')}` : providerState(open.state)],
            [r('columns.amount'), money(open.amount, open)],
            [r('columns.refunded'), money(open.refundedAmount, open)],
            [r('detail.recorded'), open.recordedAmount === null || open.recordedAmount === undefined ? '—' : money(open.recordedAmount, open)],
            [r('detail.recordedOrder'), open.recordedOrderId],
            [r('detail.paidAt'), format.dateTime(open.paidAt)],
            [r('detail.providerTransaction'), open.providerTransactionId],
            [r('detail.entity'), nameIn(entities.data, open.legalEntityId)],
            [r('columns.store'), nameIn(stores.data, open.storeId)],
            [r('columns.channel'), where(open)],
            [r('detail.intent'), open.intentId],
          ]}
        />
      )}
    </section>
  );
}
